import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth/auth-context'
import { mapRow, mapRows, toDbRow } from '@/lib/db-utils'
import type { Notification, NotificationPreference } from '@/types/notifications'

interface UseNotificationsReturn {
  notifications: Notification[]
  unreadCount: number
  loading: boolean
  error: string | null
  markAsRead: (id: string) => Promise<void>
  markAllRead: () => Promise<void>
  getPreferences: () => Promise<NotificationPreference[]>
  updatePreferences: (prefs: Partial<NotificationPreference>) => Promise<void>
  refetch: () => Promise<void>
}

export function useNotifications(): UseNotificationsReturn {
  const { user } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const isMounted = useRef(true)

  const fetchAll = useCallback(async () => {
    if (!user) {
      setNotifications([])
      setUnreadCount(0)
      setLoading(false)
      return
    }

    setLoading(true)

    const [notifRes, countRes] = await Promise.all([
      supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50),
      supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .is('read_at', null),
    ])

    if (!isMounted.current) return

    if (notifRes.error) setError(notifRes.error.message)
    else setNotifications(mapRows<Notification>(notifRes.data))

    if (countRes.error) setError(countRes.error.message)
    else setUnreadCount(countRes.count ?? 0)

    setLoading(false)
  }, [user])

  useEffect(() => {
    isMounted.current = true
    fetchAll()

    // Realtime subscription for new notifications
    if (!user)
      return () => {
        isMounted.current = false
      }

    const channel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          if (!isMounted.current) return
          const newNotification = mapRow<Notification>(payload.new as Record<string, unknown>)
          setNotifications((prev) => [newNotification, ...prev])
          setUnreadCount((prev) => prev + 1)
        },
      )
      .subscribe()

    return () => {
      isMounted.current = false
      supabase.removeChannel(channel)
    }
  }, [fetchAll, user])

  const markAsRead = async (id: string) => {
    if (!user) return
    const { error: err } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', user.id)
    if (err) {
      setError(err.message)
      throw new Error(err.message)
    }
    await fetchAll()
  }

  const markAllRead = async () => {
    if (!user) return
    const { error: err } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('user_id', user.id)
      .is('read_at', null)
    if (err) {
      setError(err.message)
      throw new Error(err.message)
    }
    await fetchAll()
  }

  const getPreferences = async (): Promise<NotificationPreference[]> => {
    if (!user) return []
    const { data, error: err } = await supabase
      .from('notification_preferences')
      .select('*')
      .eq('user_id', user.id)
    if (err) {
      setError(err.message)
      return []
    }
    return mapRows<NotificationPreference>(data)
  }

  const updatePreferences = async (prefs: Partial<NotificationPreference>) => {
    if (!user) return
    const row = toDbRow({ ...prefs, userId: user.id } as Record<string, unknown>)
    const { error: err } = await supabase
      .from('notification_preferences')
      .upsert(row, { onConflict: 'user_id,category' })
    if (err) {
      setError(err.message)
      throw new Error(err.message)
    }
  }

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllRead,
    getPreferences,
    updatePreferences,
    refetch: fetchAll,
  }
}
